import type { QueryClient } from '@tanstack/react-query'
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
} from '@tanstack/react-router'
import { AppLayout } from './components/app-layout'
import { Root } from './components/root'
import { NotFound, Splash } from './components/screens'
import { meQuery } from './lib/api'
import { partnerMeQuery } from './modules/partner-portal/api'
import { DashboardPage } from './routes/dashboard'
import { LoginPage } from './routes/login'
import { TwoFactorPage } from './routes/two-factor'

export interface RouterContext {
  queryClient: QueryClient
}

interface RedirectSearch {
  redirect?: string
}

const validateRedirect = (search: Record<string, unknown>): RedirectSearch =>
  typeof search.redirect === 'string' && search.redirect.startsWith('/') ? { redirect: search.redirect } : {}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Root,
  notFoundComponent: NotFound,
})

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: validateRedirect,
  beforeLoad: async ({ context }) => {
    if (await context.queryClient.ensureQueryData(meQuery)) throw redirect({ to: '/' })
  },
  component: LoginPage,
})

const twoFactorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/two-factor',
  validateSearch: validateRedirect,
  component: TwoFactorPage,
})

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: async ({ context, location }) => {
    const me = await context.queryClient.ensureQueryData(meQuery)
    if (!me) throw redirect({ to: '/login', search: { redirect: location.href } })
  },
  pendingComponent: Splash,
  component: AppLayout,
})

const dashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  component: DashboardPage,
})

const usersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/users',
  component: lazyRouteComponent(() => import('./routes/settings/users'), 'UsersPage'),
})

const rolesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/roles',
  component: lazyRouteComponent(() => import('./routes/settings/roles'), 'RolesPage'),
})

const auditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/audit',
  component: lazyRouteComponent(() => import('./routes/settings/audit'), 'AuditPage'),
})

const profileRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/profile',
  validateSearch: (search: Record<string, unknown>): { tab?: 'account' | 'security' } =>
    search.tab === 'security' || search.tab === 'account' ? { tab: search.tab } : {},
  component: lazyRouteComponent(() => import('./routes/profile'), 'ProfilePage'),
})

const domainsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/domains',
  component: lazyRouteComponent(() => import('./routes/settings/domains'), 'DomainsPage'),
})

const supportRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/support',
  component: lazyRouteComponent(() => import('./routes/settings/support'), 'SupportAccessPage'),
})

const billingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/billing',
  component: lazyRouteComponent(() => import('./routes/settings/billing'), 'BillingPage'),
})

// Phase 6: import a LedgerFlow business.
const importRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/import',
  component: lazyRouteComponent(() => import('./routes/settings/import'), 'ImportPage'),
})

// Phase 2: catalogue, network, finance and business settings.
const page = <TPath extends string>(
  path: TPath,
  load: () => Promise<Record<string, unknown>>,
  name: string,
) => createRoute({ getParentRoute: () => appRoute, path, component: lazyRouteComponent(load, name) })

const productsRoute = page('/products', () => import('./routes/products/index'), 'ProductsPage')
const productImportRoute = page(
  '/products/import',
  () => import('./routes/products/import'),
  'ProductImportPage',
)
const catalogRoute = page('/catalog', () => import('./routes/products/catalog'), 'CatalogSetupPage')
const partiesRoute = page('/parties', () => import('./routes/parties/index'), 'PartiesPage')
const partyRoute = page('/parties/$partyId', () => import('./routes/parties/detail'), 'PartyDetailPage')
const sitesRoute = page('/sites', () => import('./routes/sites/index'), 'SitesPage')
const employeesRoute = page('/employees', () => import('./routes/employees/index'), 'EmployeesPage')
const employeeRoute = page(
  '/employees/$employeeId',
  () => import('./routes/employees/detail'),
  'EmployeeDetailPage',
)
const accountsRoute = page('/finance/accounts', () => import('./routes/finance/accounts'), 'AccountsPage')
const journalRoute = page('/finance/journal', () => import('./routes/finance/journal'), 'JournalPage')
const bankRoute = page('/finance/bank', () => import('./routes/finance/bank'), 'BankPage')
const businessRoute = page(
  '/settings/business',
  () => import('./routes/settings/business'),
  'BusinessSettingsPage',
)
const branchesRoute = page('/settings/branches', () => import('./routes/settings/branches'), 'BranchesPage')

// Phase 3: purchases, inventory, pricing and labels.
const toNumber = (v: unknown): number | undefined => {
  const n = Number(v)
  return Number.isInteger(n) && n > 0 ? n : undefined
}
const pick = <K extends string>(search: Record<string, unknown>, keys: K[]): Partial<Record<K, number>> =>
  Object.fromEntries(keys.map((k) => [k, toNumber(search[k])]).filter(([, v]) => v !== undefined)) as Partial<
    Record<K, number>
  >

const purchasesRoute = page('/purchases', () => import('./routes/purchases/index'), 'PurchasesPage')
const newBillRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/purchases/new',
  validateSearch: (search: Record<string, unknown>): { order?: number } => pick(search, ['order']),
  component: lazyRouteComponent(() => import('./routes/purchases/bill'), 'NewBillRoute'),
})
const billRoute = page('/purchases/$billId', () => import('./routes/purchases/bill'), 'BillRoute')
const editBillRoute = page(
  '/purchases/$billId/edit',
  () => import('./routes/purchases/bill'),
  'EditBillRoute',
)
// Purchases -> Scan bill (AI reading of supplier invoices).
const scanBillsRoute = page('/purchases/scan', () => import('./routes/purchases/scan'), 'ScanBillsPage')
const scanReviewRoute = page(
  '/purchases/scan/$scanId',
  () => import('./routes/purchases/scan'),
  'ScanReviewRoute',
)
const purchaseOrdersRoute = page(
  '/purchases/orders',
  () => import('./routes/purchases/orders'),
  'PurchaseOrdersPage',
)
const purchaseReturnsRoute = page(
  '/purchases/returns',
  () => import('./routes/purchases/returns'),
  'PurchaseReturnsPage',
)
const supplierPaymentsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/purchases/payments',
  validateSearch: (search: Record<string, unknown>): { supplier?: number; bill?: number } =>
    pick(search, ['supplier', 'bill']),
  component: lazyRouteComponent(() => import('./routes/purchases/payments'), 'SupplierPaymentsRoute'),
})
const inventoryRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/inventory',
  validateSearch: (search: Record<string, unknown>): { item?: number } => pick(search, ['item']),
  component: lazyRouteComponent(() => import('./routes/inventory/index'), 'InventoryRoute'),
})
const inventoryOpsRoute = page(
  '/inventory/operations',
  () => import('./routes/inventory/operations'),
  'InventoryOperationsPage',
)
const inventoryTrackingRoute = page(
  '/inventory/tracking',
  () => import('./routes/inventory/tracking'),
  'InventoryTrackingPage',
)
const pricingRoute = page('/pricing', () => import('./routes/pricing/index'), 'PricingRulesPage')
const priceReviewRoute = page(
  '/pricing/review/$billId',
  () => import('./routes/pricing/review'),
  'PriceReviewRoute',
)
const labelsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/labels',
  validateSearch: (search: Record<string, unknown>): { bill?: number; item?: number; queue?: number } =>
    pick(search, ['bill', 'item', 'queue']),
  component: lazyRouteComponent(() => import('./routes/labels/index'), 'LabelsRoute'),
})

// Phase 4: sell — estimates, orders, invoices, POS, receipts, receivables.
interface ConvertSearch {
  estimate?: number
  order?: number
  approval?: number
}
const newDoc = (
  path: '/sales/invoices/new' | '/sales/estimates/new' | '/sales/orders/new',
  name: 'NewInvoiceRoute' | 'NewEstimateRoute' | 'NewOrderRoute',
) =>
  createRoute({
    getParentRoute: () => appRoute,
    path,
    validateSearch: (search: Record<string, unknown>): ConvertSearch =>
      pick(search, ['estimate', 'order', 'approval']),
    component: lazyRouteComponent(() => import('./routes/sales/document'), name),
  })
const salesDoc = () => import('./routes/sales/document')
const posRoute = page('/pos', () => import('./routes/pos/index'), 'PosPage')
const invoicesRoute = page('/sales/invoices', () => import('./routes/sales/list'), 'InvoicesPage')
const newInvoiceRoute = newDoc('/sales/invoices/new', 'NewInvoiceRoute')
const invoiceRoute = page('/sales/invoices/$id', salesDoc, 'InvoiceRoute')
const editInvoiceRoute = page('/sales/invoices/$id/edit', salesDoc, 'EditInvoiceRoute')
const estimatesRoute = page('/sales/estimates', () => import('./routes/sales/list'), 'EstimatesPage')
const newEstimateRoute = newDoc('/sales/estimates/new', 'NewEstimateRoute')
const estimateRoute = page('/sales/estimates/$id', salesDoc, 'EstimateRoute')
const editEstimateRoute = page('/sales/estimates/$id/edit', salesDoc, 'EditEstimateRoute')
const ordersRoute = page('/sales/orders', () => import('./routes/sales/list'), 'OrdersPage')
const newOrderRoute = newDoc('/sales/orders/new', 'NewOrderRoute')
const orderRoute = page('/sales/orders/$id', salesDoc, 'OrderRoute')
const editOrderRoute = page('/sales/orders/$id/edit', salesDoc, 'EditOrderRoute')
const returnsRoute = page('/sales/returns', () => import('./routes/sales/ops'), 'ReturnsPage')
const deliveriesRoute = page('/sales/deliveries', () => import('./routes/sales/ops'), 'DeliveriesPage')
const approvalsRoute = page('/sales/approvals', () => import('./routes/sales/ops'), 'ApprovalsPage')
const receiptsRoute = page('/receipts', () => import('./routes/receipts/index'), 'ReceiptsPage')
const receivablesRoute = page('/receivables', () => import('./routes/receivables/index'), 'ReceivablesPage')
const salesSettingsRoute = page(
  '/settings/sales',
  () => import('./routes/settings/sales'),
  'SalesSettingsPage',
)

// Phase 5: partners & SIs, consignment, margin rules, statements, channel costs.
const partnersRoute = page('/partners', () => import('./routes/partners/index'), 'PartnersPage')
const partnerRoute = page(
  '/partners/$partnerId',
  () => import('./routes/partners/detail'),
  'PartnerDetailPage',
)
const marginRulesRoute = page('/partners/rules', () => import('./routes/partners/rules'), 'MarginRulesPage')
const consignmentRoute = page(
  '/partners/consignment',
  () => import('./routes/partners/consignment'),
  'ConsignmentPage',
)
const statementsRoute = page(
  '/finance/statements',
  () => import('./routes/finance/statements'),
  'StatementsPage',
)
const channelCostsRoute = page(
  '/finance/channel-costs',
  () => import('./routes/finance/channel'),
  'ChannelCostsPage',
)

// Phase 7: reports, GST returns, report subscriptions.
const reportsRoute = page('/reports', () => import('./routes/reports/index'), 'ReportsPage')
const reportViewRoute = page('/reports/$key', () => import('./routes/reports/view'), 'ReportViewRoute')
const gstRoute = page('/gst', () => import('./routes/gst/index'), 'GstPage')
const reportSubscriptionsRoute = page(
  '/settings/report-subscriptions',
  () => import('./routes/settings/report-subscriptions'),
  'ReportSubscriptionsPage',
)

// Phase 5b: the partner portal (own sign-in, own session guard, own layout).
const partnerPortalAdminRoute = page(
  '/partners/portal',
  () => import('./routes/partners/portal'),
  'PartnerPortalAdminPage',
)
const partnerLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/partner/login',
  beforeLoad: async ({ context }) => {
    if (await context.queryClient.ensureQueryData(partnerMeQuery)) throw redirect({ to: '/partner' })
  },
  component: lazyRouteComponent(() => import('./routes/partner-portal/auth'), 'PartnerLoginPage'),
})
const partnerTwoFactorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/partner/two-factor',
  component: lazyRouteComponent(() => import('./routes/partner-portal/auth'), 'PartnerTwoFactorPage'),
})
const partnerAcceptRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/partner/accept',
  validateSearch: (search: Record<string, unknown>): { token?: string } =>
    typeof search.token === 'string' ? { token: search.token } : {},
  component: lazyRouteComponent(() => import('./routes/partner-portal/auth'), 'PartnerAcceptPage'),
})
const partnerAppRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'partner-app',
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(partnerMeQuery)
    if (!me) throw redirect({ to: '/partner/login' })
  },
  pendingComponent: Splash,
  component: lazyRouteComponent(() => import('./routes/partner-portal/layout'), 'PartnerLayout'),
})
const partnerPage = <TPath extends string>(
  path: TPath,
  load: () => Promise<Record<string, unknown>>,
  name: string,
) => createRoute({ getParentRoute: () => partnerAppRoute, path, component: lazyRouteComponent(load, name) })
const partnerHomeRoute = partnerPage(
  '/partner',
  () => import('./routes/partner-portal/home'),
  'PartnerHomePage',
)
const partnerCatalogueRoute = partnerPage(
  '/partner/catalogue',
  () => import('./routes/partner-portal/catalogue'),
  'PartnerCataloguePage',
)
const partnerCustomersRoute = partnerPage(
  '/partner/customers',
  () => import('./routes/partner-portal/customers'),
  'PartnerCustomersPage',
)
const partnerEstimatesRoute = partnerPage(
  '/partner/estimates',
  () => import('./routes/partner-portal/estimates'),
  'PartnerEstimatesPage',
)
const partnerNewEstimateRoute = createRoute({
  getParentRoute: () => partnerAppRoute,
  path: '/partner/estimates/new',
  validateSearch: (search: Record<string, unknown>): { customer?: number } => pick(search, ['customer']),
  component: lazyRouteComponent(() => import('./routes/partner-portal/estimates'), 'PartnerNewEstimatePage'),
})
const partnerEstimateRoute = partnerPage(
  '/partner/estimates/$estimateId',
  () => import('./routes/partner-portal/estimates'),
  'PartnerEstimatePage',
)
const partnerOrdersRoute = partnerPage(
  '/partner/orders',
  () => import('./routes/partner-portal/orders'),
  'PartnerOrdersPage',
)
const partnerOrderRoute = partnerPage(
  '/partner/orders/$orderId',
  () => import('./routes/partner-portal/orders'),
  'PartnerOrderPage',
)
const partnerInvoicesRoute = partnerPage(
  '/partner/invoices',
  () => import('./routes/partner-portal/orders'),
  'PartnerInvoicesPage',
)
const partnerPaymentsRoute = partnerPage(
  '/partner/payments',
  () => import('./routes/partner-portal/payments'),
  'PartnerPaymentsPage',
)
const partnerStatementRoute = partnerPage(
  '/partner/statement',
  () => import('./routes/partner-portal/payments'),
  'PartnerStatementPage',
)
const partnerProfileRoute = partnerPage(
  '/partner/profile',
  () => import('./routes/partner-portal/profile'),
  'PartnerProfilePage',
)

// Public: the product page behind a label's QR code (no sign-in).
const publicProductRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/p/$code',
  component: lazyRouteComponent(() => import('./routes/public/product'), 'PublicProductRoute'),
})

// Public: a phone opened from a POS counter's QR code, used as a wireless scanner.
const posScanRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pos/scan/$token',
  component: lazyRouteComponent(() => import('./routes/public/pos-scan'), 'PosScanRoute'),
})

// Public: exchanges a one-time Bixo support link for a read-only session.
const impersonateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/impersonate',
  validateSearch: (search: Record<string, unknown>): { token?: string } =>
    typeof search.token === 'string' ? { token: search.token } : {},
  component: lazyRouteComponent(() => import('./routes/impersonate'), 'ImpersonatePage'),
})

const routeTree = rootRoute.addChildren([
  loginRoute,
  twoFactorRoute,
  impersonateRoute,
  publicProductRoute,
  posScanRoute,
  appRoute.addChildren([
    dashboardRoute,
    usersRoute,
    rolesRoute,
    auditRoute,
    profileRoute,
    domainsRoute,
    supportRoute,
    billingRoute,
    importRoute,
    productsRoute,
    productImportRoute,
    catalogRoute,
    partiesRoute,
    partyRoute,
    sitesRoute,
    employeesRoute,
    employeeRoute,
    accountsRoute,
    journalRoute,
    bankRoute,
    businessRoute,
    branchesRoute,
    purchasesRoute,
    newBillRoute,
    billRoute,
    editBillRoute,
    purchaseOrdersRoute,
    scanBillsRoute,
    scanReviewRoute,
    purchaseReturnsRoute,
    supplierPaymentsRoute,
    inventoryRoute,
    inventoryOpsRoute,
    inventoryTrackingRoute,
    pricingRoute,
    priceReviewRoute,
    labelsRoute,
    posRoute,
    invoicesRoute,
    newInvoiceRoute,
    invoiceRoute,
    editInvoiceRoute,
    estimatesRoute,
    newEstimateRoute,
    estimateRoute,
    editEstimateRoute,
    ordersRoute,
    newOrderRoute,
    orderRoute,
    editOrderRoute,
    returnsRoute,
    deliveriesRoute,
    approvalsRoute,
    receiptsRoute,
    receivablesRoute,
    salesSettingsRoute,
    partnersRoute,
    partnerRoute,
    marginRulesRoute,
    consignmentRoute,
    statementsRoute,
    channelCostsRoute,
    reportsRoute,
    reportViewRoute,
    gstRoute,
    reportSubscriptionsRoute,
    partnerPortalAdminRoute,
  ]),
  partnerLoginRoute,
  partnerTwoFactorRoute,
  partnerAcceptRoute,
  partnerAppRoute.addChildren([
    partnerHomeRoute,
    partnerCatalogueRoute,
    partnerCustomersRoute,
    partnerEstimatesRoute,
    partnerNewEstimateRoute,
    partnerEstimateRoute,
    partnerOrdersRoute,
    partnerOrderRoute,
    partnerInvoicesRoute,
    partnerPaymentsRoute,
    partnerStatementRoute,
    partnerProfileRoute,
  ]),
])

export function buildRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
    defaultPreloadStaleTime: 0,
    scrollRestoration: true,
  })
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof buildRouter>
  }
}
